import React, { useEffect, useMemo, useState } from 'react'
import axios from "axios";
import jsPDF from 'jspdf';
import moment from 'moment';
import Papa from "papaparse";
import { Link, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from "xlsx";
import { usePermissionGuardRedirect } from '../../../../hooks/usePermissionGuard';
import { deleteVersion, getVersionList } from '../../../../services/ProgramService';
import { ExpendableColumnByHeight } from '../../../../utils/commonFunction';
import { TABLE_DATE_FORMAT } from '../../../../utils/Constants';
import PermissionsGate from '../../../../utils/permissionGate';
import DataTableComponent from '../../../common/DataTableComponent';

function VersionTable() {
    const { id } = useParams();
    const [search, setSearch] = useState("");
    const [data, setData] = useState();
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(Date.now());
    const [tableState, setTableState] = useState({
        page: 1,
        perPage: 10,
        sortKey: "created_at",
        sortOrder: "desc",
    });
    const [totalRows, setTotalRows] = useState(0);
    
    usePermissionGuardRedirect(['pmcversionview']);

    useEffect(() => {
        const source = axios.CancelToken.source();

        const fetchVersionList = async () => {
            setLoading(true);

            try {
                const payload = {
                    pmc_id: id,
                    search: search,
                    page: tableState.page,
                    limit: tableState.perPage,
                    sort: tableState.sortOrder,
                    key: tableState.sortKey,
                    exportStatus: "false",
                };

                const res = await getVersionList(payload, source.token);
                if (res.status == "200") {
                  setLoading(false);
                    setData(res.data.getVersionList.resData || []);
                    setTotalRows(res.data.getVersionList?.pagination?.total || 0)
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error(error);
                    setLoading(false);
                }
            }
        };

        fetchVersionList();

        return () => {
            source.cancel("Component unmounted");
        };
    }, [id, search, tableState,refresh]); 

    const handleDelete = (id, activeVersion) => {
        if (data?.length === 1 || activeVersion) {
            Swal.fire({
                icon: "warning",
                text: activeVersion ? "You cannot delete the active PMC version." : "You cannot delete the only remaining PMC version.",
            });
        } else {
            Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, remove it!",
            }).then((result) => {
                if (result.isConfirmed) {
                    deleteVersion({ id })
                        .then((res) => {
                            Swal.fire("Deleted!", "Version has been removed.", "success");
                            setRefresh(Date.now());
                        })
                        .catch((error) => console.log(error));
                }
            });
        }
    };

    const exportData = (fileType, fileName) => {
        let exportData = data;

        const header = ["Name", "Linked Programmes", "Created", "Last Modified", "Notes"];

        Swal.fire({
            title: "File downloading",
            onOpen: function () {
                Swal.showLoading();
            },
        });

        const payload = {
            pmc_id: id,
            search: search,
            page: tableState.page,
            limit: tableState.perPage,
            sort: tableState.sortOrder,
            key: tableState.sortKey,
            exportStatus: "true",
        };

        getVersionList(payload).then((res) => {
            exportData = res.data?.getVersionList?.resData || [];
            exportData = exportData?.map((row) => ({
                ...row,
                Name: row?.name ?? "-",
                "Linked Programmes": (row?.linkedProgramCount == null || row?.linkedProgramCount == undefined) ? "-" : row.linkedProgramCount,
                Created: row?.created_at ? moment(row.created_at).format(TABLE_DATE_FORMAT) : "-",
                "Last Modified": row?.updated_at ? moment(row.updated_at).format(TABLE_DATE_FORMAT) : "-",
                Notes: row?.notes ?? "-"
            }));

            if (fileType === "csv") {
                const csvString = Papa.unparse({ fields: header, data: exportData });
                const blob = new Blob([csvString], { type: "text/csv;charset=utf-8," });

                const blobURL = window.URL.createObjectURL(blob);

                // Create new tag for download file
                const anchor = document.createElement("a");
                anchor.download = fileName;
                anchor.href = blobURL;
                anchor.dataset.downloadurl = [
                    "text/csv",
                    anchor.download,
                    anchor.href,
                ].join(":");
                anchor.click();

                // Remove URL.createObjectURL. The browser should not save the reference to the file.
                setTimeout(() => {
                    // For Firefox it is necessary to delay revoking the ObjectURL
                    URL.revokeObjectURL(blobURL);
                }, 1000);
                Swal.close();
            } else if (fileType === "xlsx") {
                const compatibleData = exportData.map((row) => {
                    const obj = {};
                    header.forEach((col, index) => {
                        obj[col] = row[col];
                    });
                    return obj;
                });

                let wb = XLSX.utils.book_new();
                let ws1 = XLSX.utils.json_to_sheet(compatibleData, {
                    header,
                });
                XLSX.utils.book_append_sheet(wb, ws1, "React Table Data");
                XLSX.writeFile(wb, `${fileName}.xlsx`);
                Swal.close();
                // Returning false as downloading of file is already taken care of
                return false;
            }
            if (fileType === "pdf") {
                const compatibleData = exportData.map((row) => {
                    return [
                        row["Name"],
                        row["Linked Programmes"],
                        row["Created"],
                        row["Last Modified"],
                        row["Notes"],
                    ];
                });
                const doc = new jsPDF();
                doc.autoTable({
                    head: [header],
                    body: compatibleData,
                    styles: {
                        minCellHeight: 10,
                        minCellWidth: 5,
                        halign: "left",
                        fontSize: 8,
                    },
                });
                doc.save(`${fileName}.pdf`);
                Swal.close();
                return false;
            }
        }).catch((err) => {
            console.log(err)
        })
    };

    const columns = useMemo(() => [
        {
            name: "Name",
            selector: "name",
            minwidth: "100px",
            sortField: "name",
            sortable: true,
            cell: (row) => (
                    <div className='d-flex align-items-center '>
                        <Link
                            to={`/program-management-center/programmes/versions/details?versionId=${row?.id}&programmeId=${id}`}
                            title={row?.name}
                            className="as-text-blue curser feature-name version_list "
                        >
                            <span title={row?.name} className="textLimit100">{row?.name} </span>

                        </Link>
                    {Number(row?.version_id) === Number(row?.id) ? (
                        <i className="fa fa-check-circle pl-1 bg-green-icon"></i>
                    ) : null}
                </div>
            ),
        },
        {
            name: "#Linked Programmes",
            selector: "linkedProgramcount",
            sortField: "linkedProgramCount",
            sortable: true,
            cell: (row) => (
                <div>
                    <span>{row?.linkedProgramCount ?? "-"}</span>
                </div>
            )
        },
        {
            name: "Created",
            selector: "created_at",
            sortField: "created_at",
            minwidth: "100px",
            sortable: true,
            cell: (row, index) => (
                <div className="ticket-id">
                    <span
                        title={row?.created_at ? moment(row.created_at).format(TABLE_DATE_FORMAT) : "-"}
                        className="overflow-ellipsis2"
                    >
                        {row?.created_at ? moment(row.created_at).format(TABLE_DATE_FORMAT) : "-"}
                    </span>
                </div>
            ),

        },
        {
            name: "Last Modified",
            selector: "last_modified",
            sortField: "updated_at",
            minwidth: "100px",
            sortable: true,
            cell: (row, index) => (
                <div className="ticket-id">
                    <span title={row?.updated_at ? moment(row.updated_at).format(TABLE_DATE_FORMAT) : "-"} className="overflow-ellipsis2">
                        {row?.updated_at ? moment(row.updated_at).format(TABLE_DATE_FORMAT) : "-"}
                    </span>
                </div>
            ),
        },
        {
            name: "Notes",
            selector: "notes",
            sortField: "notes",
            sortable: true,
            minwidth: "100px",
            cell: (row) => {

                return (
                    <ExpendableColumnByHeight text={row?.notes}/>
                );
            },
        },
        {
            name: "Actions",
            selector: "",
            cell: (row) => (
                <div className="assessment-08 btn-dropdown-grp">
                    <div className="as-buttons">
                        <Link
                            to={`/program-management-center/programmes/versions/details?versionId=${row?.id}&programmeId=${id}`}
                            className="btn btn-primary rounded-circle"
                            title="Open"
                        >
                            <i className="fal fa-folder-open"></i>
                        </Link>
                        <div className="dropdown btn-dropdown-item">
                            <button className="btn btn-primary rounded-circle dropdown-toggle" type="button" title="More" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                <i className="fal fa-ellipsis-h-alt"></i>
                            </button>
                            <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                <PermissionsGate RenderError={() => (
                                    <button
                                        className="btn btn-primary rounded-circle"
                                        title="Duplicate"
                                        disabled
                                    >
                                        <i className="fal fa-copy"></i>
                                    </button>
                                )} scopes={["pmcversionadd"]}>
                                    <Link
                                        className="btn btn-primary rounded-circle"
                                        title="Duplicate"
                                        to={`/program-management-center/programmes/versions/clone?versionId=${row?.id}&programmeId=${id}`}
                                    >
                                        <i className="fal fa-copy"></i>
                                    </Link>
                                </PermissionsGate>
                                <PermissionsGate
                                    scopes={["pmcversiondelete"]}
                                    RenderError={() => (
                                        <button
                                            className="btn btn-danger rounded-circle"
                                            title="Delete"
                                            disabled
                                        >
                                            <i className="fal fa-trash-alt"></i>
                                        </button>
                                    )}
                                >
                                    <button
                                        className="btn btn-danger rounded-circle"
                                        title="Delete"
                                        onClick={() => handleDelete(row.id, Number(row?.version_id) === Number(row?.id))}
                                    >
                                        <i className="fal fa-trash-alt"></i>
                                    </button>
                                </PermissionsGate>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
    ]);
    return (
        <DataTableComponent
            data={data}
            columns={columns}
            loading={loading}
            search={search}
            setSearch={setSearch}
            state={tableState}
            setState={setTableState}
            totalRows={totalRows}
            exportFunction={exportData}
            exportFileName={"Versions List"}
            tableButton={[
                <PermissionsGate
                    RenderError={() => (
                        <button
                            className="btn btn-primary"
                            title="Create New"
                            disabled
                        >
                            <i className="fal fa-plus"></i>Create New
                        </button>
                    )}
                    scopes={["pmcversionadd"]}
                >
                    <Link to={`/program-management-center/programmes/versions/create?programmeId=${id}`} >
                        <button className="btn btn-primary" title="Create New">
                            <i className="fal fa-plus"></i>Create New
                        </button>
                    </Link>
                </PermissionsGate>
            ]}
        />
    );
}

export default VersionTable
